// 3rd party
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatExpansionPanel, MatTabGroup } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { Subscription } from 'rxjs/Rx';
import * as _ from 'lodash';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { InstrumentListComponent } from 'app/instruments/instrument-list/instrument-list.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';
import { AppComponent } from 'app/app.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { EReferenceModeType } from 'app/shared/models/cal-criterion.model';
import { StoredFile } from 'app/shared/models/stored-file.model';
import { Instrument } from 'app/instruments/shared/instrument.model';
import { CriterionAcceptance } from 'app/shared/models/criterion-acceptance.model';
import { InstrumentBand } from 'app/instruments/shared/instrument-band.model';
import { Calibration } from 'app/calibrations/shared/calibration.model';

// services
import { InstrumentService } from 'app/instruments/shared/instrument.service';
import { CriterionService } from 'app/shared/services/criterion.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { CalibrationService } from 'app/calibrations/shared/calibration.service';

@Component({
  selector: 'app-insturment',
  templateUrl: './instrument.component.html',
  styleUrls: ['./instrument.component.css']
})
export class InstrumentComponent implements OnInit, OnDestroy {

  @ViewChild('instrumentPanel') instrumentPanel: MatExpansionPanel;
  @ViewChild('matTabGroup') matTabGroup: MatTabGroup;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  attachments: any[] = new Array<any>();
  certificates: any[] = new Array<any>();
  checkedAttachments: any;
  checkedCertificates: any;
  chkAllAttachments: boolean = false;
  chkAllCertificates: boolean = false;
  columnTag: string;
  criteria: CriterionAcceptance[] = new Array<CriterionAcceptance>();
  isCheckingAll: boolean = false;
  isLoading = false;
  isMobile = false;
  instrument: Instrument = new Instrument();
  ReferenceModeType: typeof EReferenceModeType = EReferenceModeType;
  subscribe1: Subscription;
  subscribe2: Subscription;

  constructor(
    private activeRoute: ActivatedRoute,
    public app: AppComponent,
    private calibrationService: CalibrationService,
    public criterionService: CriterionService,
    private dialog: DialogsService,
    public instrumentListComponent: InstrumentListComponent,
    public instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    public router: Router,
    public storedFileService: StoredFileService,
    public translate: TranslateService,
    private media: ObservableMedia
  ) { }

  ngOnInit(): void {
    this.columnTag = localStorage.getItem('instrumentTagName');
    this.subscribe2 = this.media.subscribe((change: MediaChange) => {
      this.isMobile = change.mqAlias === 'xs';
    });
    this.instrumentPanel.expanded = true;

    this.subscribe1 = this.activeRoute.params.subscribe(params => {
      let id = params['identifier'];
      if (!id) {
        return;
      }
      this.instrumentListComponent.sidenavRight.open();
      this.instrumentService.get(id).subscribe(res => {
        this.instrument = Helper.deserialize(res.text())[0];
        this.loadAttachments();
        this.loadCertificates();
        this.loadBands();
        if (this.matTabGroup && this.matTabGroup.selectedIndex === 1) {
          this.loadCriteria();
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.subscribe1) {
      this.subscribe1.unsubscribe();
    }
    this.instrumentListComponent.sidenavRight.close();
  }

  checkAllAttachments(e) {
    this.isCheckingAll = true;
    this.attachments.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  checkAllCertificates(e) {
    this.isCheckingAll = true;
    this.certificates.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedCertificates = this.certificates.filter(item => item.checked).length;
  }

  close() {
    this.instrumentListComponent.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/instruments/`]);
  }

  downloadAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let files: any[] = new Array<any>();
    let fileName: string = '';

    lst.forEach(element => {
      (fileName = this.storedFileService.formatUserFileName(element.UserFileName));
      files.push({ identifier: element.Identifier, file: fileName ? fileName : element.SystemFileName, size: this.storedFileService.formatFileSize(element.size), systemFileName: element.SystemFileName, userFileName: element.UserFileName });
    });

    this.storedFileService.download(files)
      .subscribe(data => {
        if (data) {
          let fileName = files.length === 1 ? files[0].file : this.instrument.Code;
          FileSaver.saveAs(data, fileName);
          this.isLoading = false;
        } else {
          this.isLoading = false;
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  downloadCertificates() {
    this.isLoading = true;
    let selectedCalibrations = this.certificates.filter(d => d.checked) as Calibration[];
    let params = selectedCalibrations.map(calibration => {
      return {
        identifier: calibration.Identifier,
        tableName: calibration.TableName
      };
    });

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, selectedCalibrations.length > 1 ? 'certificados.cal' : `${selectedCalibrations[0].Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', selectedCalibrations.length > 1 ? 'Message.DownloadCertificatesError' : 'Message.DownloadCertificateError');
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', selectedCalibrations.length > 1 ? 'Message.DownloadCertificatesError' : 'Message.DownloadCertificateError');
      });
  }

  fillBand(band: InstrumentBand) {
    this.translate.get('Translate.BandsAmplitude').subscribe((val) => {
      band.BandDescription = Helper.getRangeValueFormated(band.Name, band.MinValue, band.MaxValue, band.Precision, band.MeasurementUnit ? band.MeasurementUnit.Abbreviation : null, val);
    });
  }

  fillCriterion(criterion: CriterionAcceptance) {
    this.translate.get('Translate.BandsAmplitude').subscribe((val) => {
      let abbreviation = '%';
      if (criterion.Type === EReferenceModeType.AbsoluteValue)
        abbreviation = criterion.MeasurementUnit ? criterion.MeasurementUnit.Abbreviation : null;
      criterion.CriterionDescription = Helper.getRangeValueFormated(null, criterion.MaxErrorLower, criterion.MaxErrorMore, criterion.Precision, abbreviation, val);
    });
  }

  getAttachmentName(sf: StoredFile): string {
    let name = this.storedFileService.formatUserFileName(sf.UserFileName);
    return name ? name : sf.SystemFileName;
  }

  getFormattedDate(date) {
    if (!date) {
      return;
    } else {
      return new Date(date);
    }
  }

  getStatusIcon(status: number) {
    return this.instrumentService.getStatusIcon(status);
  }

  getStatusDescription(status: number) {
    return this.instrumentService.getStatusDescription(status);
  }

  loadAttachments() {
    this.isLoading = true;

    let lstFilters = '"Instrument.Identifier_=\'' + this.instrument.Identifier + '\'","IsAttach","VisibleWebview_=1"';
    let lstFields = '"SystemFileName","UserFileName","Size","Instrument.Code"';

    this.storedFileService
      .get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        this.isLoading = false;
        this.attachments = values.json();

        if (this.attachments.length > 0) {
          this.attachments.forEach(element => {
            element.checked = false;
          });
        } else {
          this.attachments = null;
        }
      }, err => {
        this.isLoading = false;
        this.attachments = null;
      });
  }

  loadCertificates() {
    this.isLoading = true;
    let lstFilters = '"Instrument.Identifier_=\'' + this.instrument.Identifier + '\'","Status_=1"';

    this.calibrationService.get(null, null, null, null, null, null, lstFilters, null)
      .subscribe(values => {
        this.isLoading = false;
        this.certificates = values.json();
        this.certificates = _.orderBy(this.certificates, 'Date', 'desc');

        if (this.certificates.length > 0) {
          this.certificates.forEach(element => {
            element.checked = false;
          });
        } else {
          this.certificates = null;
        }
      }, err => {
        this.isLoading = false;
        this.certificates = null;
      });
  }

  loadCriteria() {
    this.isLoading = true;
    this.criterionService
      .getFromInstrument(this.instrument.Identifier, this.app.currentContact.Customer.Identifier)
      .subscribe(values => {
        this.isLoading = false;
        this.criteria = values ? values as CriterionAcceptance[] : null;
        if (this.criteria !== null)
          this.criteria.forEach(c => {
            this.fillCriterion(c);
            this.fillBand(c.InstrumentBand);
          });
      }, err => {
        this.isLoading = false;
        this.criteria = null;
      });
  }

  loadBands() {
    this.isLoading = true;
    this.instrumentService.getbands([this.instrument]).subscribe(res => {
      this.isLoading = false;
    });
  }

  onAttachmentSelecionChange(a) {
    if (!this.isCheckingAll) {

      if (this.attachments.filter(item => item.checked).length !== this.attachments.length && this.chkAllAttachments) {
        this.chkAllAttachments = false;
      }
      if (this.attachments.filter(item => item.checked).length === this.attachments.length && !this.chkAllAttachments) {
        this.chkAllAttachments = true;
      }

      this.checkedAttachments = this.attachments.filter(item => item.checked).length;
    }
  }

  onCertificateSelecionChange(a) {
    if (!this.isCheckingAll) {

      if (this.certificates.filter(item => item.checked).length !== this.certificates.length && this.chkAllCertificates) {
        this.chkAllCertificates = false;
      }
      if (this.certificates.filter(item => item.checked).length === this.certificates.length && !this.chkAllCertificates) {
        this.chkAllCertificates = true;
      }

      this.checkedCertificates = this.certificates.filter(item => item.checked).length;
    }
  }

  openAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let file: any[] = new Array<any>();
    let fileName = this.storedFileService.formatUserFileName(lst[0].UserFileName);
    file.push({ identifier: lst[0].Identifier, file: fileName ? fileName : lst[0].SystemFileName, size: this.storedFileService.formatFileSize(lst[0].size), systemFileName: lst[0].SystemFileName, userFileName: lst[0].UserFileName });

    this.storedFileService.download(file)
      .subscribe(data => {
        if (data) {
          const blob = new Blob([data], { type: data.type });

          if (blob.type === 'application/pdf') {
            this.pdfViewer.pdfSrc = blob;
            this.pdfViewer.refresh();
          } else {
            const fileUrl = URL.createObjectURL(data);
            const newWindow = window.open(fileUrl);

            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              this.dialog.openPopupDialog();
            }
          }

          this.isLoading = false;
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
          this.isLoading = false;
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  openCertificate(download: boolean) {
    this.isLoading = true;

    let lst = this.certificates.filter(item => item.checked);
    let identifiers = lst.map(obj => obj.Identifier);
    let lstFilters = '"Calibration.Identifier_in(\'' + identifiers.join('\',\'') + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        storedFiles = values.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError'); this.isLoading = false;
        } else {
          this.storedFileService.download(storedFiles as StoredFile[])
            .subscribe(data => {
              if (data) {
                if (data.type === 'application/zip') {
                  FileSaver.saveAs(data, 'Certificados' + '.zip');
                } else {
                  const file = new Blob([data], { type: data.type });

                  this.storedFileService.view(file, download, this.pdfViewer, `Certificado ${storedFiles[0].Calibration.Code}`);

                }
                this.isLoading = false;
              } else {
                this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError');
                this.isLoading = false;
              }
            }, () => {
              this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
              this.isLoading = false;
            });
        }
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
        this.isLoading = false;
      });
  }

  /**
	 * Aciona o evento na mudança de Tab
	 * @param tab md-tab selecionada
	 */
  tabChanged(tab): void {
    if (tab.index === 1) {
      // So carrega os criterios na primeira vez que abre a aba
      if (this.criteria && this.criteria.length > 0) {
        return;
      }
      this.loadCriteria();
    }
  }

  sortData(data) {
    this.certificates = _.orderBy(this.certificates, [data.orderBy, 'Code'], [data.sortOrder, 'asc']);
  }
}



// WEBPACK FOOTER //
// ./src/app/instruments/instrument/instrument.component.ts